// @flow

import find from 'lodash/find';
import React, { useCallback } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { AddCartItemButton } from 'shared_data/providers/queries/AddCartItem';
import { AnimatedButton } from '@riseart/common';
import { BagIcon } from '@riseart/icons';
import { selectAuthShippingCountryCode } from 'shared_services/redux/selectors/auth';
import READ_ART_SKUS_QUERY from 'shared_data/queries/art/readSkus.graphql';

type Props = {
  artId: number,
  storeCode: string,
  skuRentId: number,
  onLoading?: Function,
};

/**
 * CartActionRentButton
 *
 * @param {Props} props
 * @returns {Node}
 */
export function CartActionRentButton({ artId, storeCode, skuRentId, onLoading }: Props): Node {
  const { formatMessage } = useIntl();
  const shippingCountry = useSelector(selectAuthShippingCountryCode);
  const [getArtSkus] = useLazyQuery(READ_ART_SKUS_QUERY, {
    skip: !artId || !storeCode,
    variables: {
      artId: `${artId}`,
      store: storeCode,
      ...(shippingCountry ? { shippingCountry } : null),
    },
    fetchPolicy: 'no-cache',
  });

  const handleLoading = useCallback(
    (isLoading: boolean) => {
      if (typeof onLoading === 'function') {
        onLoading(isLoading);
      }
    },
    [onLoading],
  );

  const handleClick = (onClick: any) => () => {
    handleLoading(true);

    // Get art first to get art insurance options
    getArtSkus()
      .then((response) => {
        const { listArtSkus: data } = (response && response.data) || {};

        if (!data || !data.items) {
          handleLoading(false);
          return;
        }

        const rentDetails = find(data.items, { type: 'REN' }) || {};
        const insuranceOption: Object = find(rentDetails.options, { isInsurance: true }) || {};
        const insuranceOptionStore: Object = find(insuranceOption.values, { storeCode }) || {};

        onClick(
          insuranceOption.id
            ? { insurance: [{ id: insuranceOption.id, values: [insuranceOptionStore.id] }] }
            : null,
        );
      })
      .catch(() => {
        handleLoading(false);
      });
  };

  return (
    <AddCartItemButton cartId="rent" qty={1} skuId={skuRentId} onLoading={onLoading}>
      {({ loading, onClick }) => (
        <AnimatedButton
          icon={<BagIcon />}
          isLoading={loading}
          onClick={handleClick(onClick)}
          title={formatMessage({ id: 'components.art.rent' })}
        />
      )}
    </AddCartItemButton>
  );
}
